import { action, computed, observable, makeObservable } from 'mobx'
import api from 'api.js'
import _ from 'lodash'
import { LatLng } from 'leaflet'
import constants, { selectionTypes } from 'constants.js'
import { removeHtml } from 'utils'
import ErrorStore, { catchError } from './ErrorStore'
import { dataStore, selectionStore, uiStore } from './UIStore'
import Position from './common/Position'
import i18next from '../i18n'

const BOUNDS_BUFFER = 0.5

// An individual marker within a markerfile.
export class UserMarkerfileMarker {
  @observable id
  @observable name
  @observable description
  @observable position
  @observable markerfile
  @observable selected = false
  @observable additional_info = []

  bounds () {
    return [
      [this.position.latitude - BOUNDS_BUFFER, this.position.longitude - BOUNDS_BUFFER],
      [this.position.latitude + BOUNDS_BUFFER, this.position.longitude + BOUNDS_BUFFER]
    ]
  }

  @action toggleSelected () {
    if (this.markerfile.exceededLimit) {
      this.selected = false
    } else {
      this.selected = !this.selected
    }
  }

  constructor ({ id, name, description, latLng, markerfile }) {
    makeObservable(this)
    this.id = id
    this.name = name || id
    this.description = description
    this.position = new Position()
    this.position.latLng = latLng
    this.markerfile = markerfile
  }
}

// A sheet within a markerfile this user has uploaded
export class UserMarkerfile {
  @observable id
  @observable _selectedLevelIndex = 0
  @observable storage_file
  @observable markers
  @observable editMode
  @observable selectedMarker = null
  @observable loading = false
  @observable loaded = false
  @observable group
  @observable additional_info = []

  constructor ({ id, name, storage_file, markers }) {
    makeObservable(this)
    this.id = id
    this.storage_file = storage_file
    this.markers = markers || []
    this.group = selectionStore.data.group
  }

  @computed get name () {
    const filename = _.last(this.storage_file.split('/'))
    const ext = this.storage_file.split('.').pop()
    return filename.replace(/[0-9]{10}.(xlsx|csv)$/, `.${ext}`)
  }

  @computed get allSelected () {
    return this.markers.length > 0 && _.every(this.markers, { selected: true })
  }

  @computed get targetGroup () {
    return this.group ||
      (selectionStore && selectionStore.data && selectionStore.data.group) ||
      (dataStore && dataStore.selectedAddGroup) ||
      (dataStore && dataStore.targetGroups && dataStore.targetGroups[0]) ||
      null
  }

  @computed get selectedCount () {
    return _.filter(this.markers, (m) => m.selected).length
  }

  @computed get exceededLimit () {
    const group = this.targetGroup
    const savedCount = (group && group.savedMarkers) ? group.savedMarkers.length : 0
    const maxMarkers = (uiStore && uiStore.user && uiStore.user.limits && uiStore.user.limits.max_markers) || 1000
    return this.selectedCount + savedCount >= maxMarkers
  }

  @computed get willExceededLimit () {
    const group = this.targetGroup
    const savedCount = (group && group.savedMarkers) ? group.savedMarkers.length : 0
    const maxMarkers = (uiStore && uiStore.user && uiStore.user.limits && uiStore.user.limits.max_markers) || 1000
    return this.markers.length + savedCount > maxMarkers
  }

  selectAll () {
    if (this.willExceededLimit) {
      return
    }

    _.each(this.markers, item => { item.selected = true })
  }

  deselectAll () {
    _.each(this.markers, item => { item.selected = false })
  }

  async deleteMarkerfile () {
    await api.deleteMarkerfile(this.id)
  }

  @catchError async load () {
    if (!this.loaded) {
      this.loading = true
      const response = await api.getUserMarkerfile(this.id)
      const results = response.data.markers
      this.markers.replace(_(results).map(marker => new UserMarkerfileMarker({
        id: marker.id,
        name: marker.name || marker.id,
        description: marker.description,
        latLng: new LatLng(marker.latitude, marker.longitude),
        markerfile: this
      })).sortBy('name').value())
      this.markers[0] && uiStore.setBoundsToItem(this.markers[0])
      this.loading = false
      this.loaded = true
    }
  }

  async uploadSelected () {
    uiStore.processing = true
    const ids = _.filter(this.markers, (m) => m.selected).map(m => m.id)
    await api.postUploadMarkerfileMarkers({ target_group: this.group.pk, stored_markers_list: ids })
    selectionStore.clear()
    await this.group.loadSavedMarkers()
    uiStore.processing = false
  }
}

// The top-level list of markerfiles this user has uploaded
class UserMarkerfileList {
  @observable markerfiles = []
  @observable choosingFile = false
  @observable _chosenFile = null
  @observable _selectedFile = null
  @observable invalid = observable.map()
  @observable loading = true
  @observable error
  @observable additional_info = []

  constructor () {
    makeObservable(this)
  }

  @computed get chosenFile () {
    return this._chosenFile
  }

  @computed get markerfilesCount () {
    return this.markerfiles.length
  }

  @computed get selectedFile () {
    return this._selectedFile
  }

  @computed get hasExceededMarkerfilesLimit () {
    return uiStore.user.limits.max_markerfiles <= this.markerfiles.length
  }

  @computed get selectedSheet () {
    return this.selectedFile ? this.selectedFile.selectedMarker : null
  }

  set selectedSheet (sheet) {
    if (this.selectedFile) {
      this.selectedFile.selectedMarker = sheet
    }
  }

  async deleteMarkerfile () {
    const file = this.selectedFile
    this.loading = true
    this.selectedSheet = null
    this.selectedFile = null
    await file.deleteMarkerfile()
    await this.load()
  }

  set selectedFile (file) {
    this._selectedFile = file
  }

  set chosenFile (file) {
    if (this.validate(file)) {
      this._chosenFile = file
    } else {
      this.choosingFile = false
    }
  }

  validate (file) {
    this.invalid.clear()
    if (file !== null && file.size > constants.MAX_SHAPEFILE_SIZE) {
      this.invalid.set('file', i18next.t('GLOBAL.MARKERFILE_TOO_LARGE'))
    }
    if (file !== null && !(/\.(csv|xlsx)$/).test(file.name)) {
      this.invalid.set('file', i18next.t('GLOBAL.MARKERFILE_NOT_CSV'))
    }

    return this.invalid.size === 0
  }

  @catchError @action async load () {
    this.loading = true
    const response = await api.getUserMarkerfiles()
    this.markerfiles.replace(response.data.map(result =>
      new UserMarkerfile(result)
    ))
    dataStore.markerfilesCount = this.markerfiles.length
    this.loading = false
  }

  async upload () {
    const data = new window.FormData()
    data.append('storage_file', this.chosenFile)
    try {
      const res = await api.postUploadMarkerfile(data)
      if (res.data.error_status) {
        this.error = res.data.message
      }
      await this.load()
      this.chosenFile = null
      this.choosingFile = false
    } catch (e) {
      this.chosenFile = null
      this.choosingFile = false
      if (e.response && e.response.status === 400) {
        const res = e.response.data
        const message = (res.storage_file || res.non_field_errors)[0]
        this.invalid.set('file', removeHtml(message))
      } else {
        ErrorStore.error(e)
      }
    }
  }

  get selection () {
    this.error = null
    return {
      item: this,
      type: selectionTypes.USER_MARKERFILE_LIST,
      data: { focusedInteraction: false }
    }
  }
}

export default UserMarkerfileList
